import { IPublicClientApplication } from "@azure/msal-browser";
import { ConfigurationService } from "../ConfigurationService";
import { HttpRequestWrapper } from "../HttpRequestWrapper";
import { Logger } from "../Logger";
import { OfficeWrapper } from "../OfficeWrapper";
import { AnalyticsManager } from "../AnalyticsManager";
import { ProjectFolderConnection } from "../../models/projects/ProjectSettingsResponse";
import { getMsalClientKey, toMsalAuthority } from "../../helpers/ProjectFolderConnectionHelpers";
import SharePointConsentDialog from "../../components/login/SharePointConsentDialog";
import {
    createNestableSharePointMsalClient,
    createSharePointMsalClient,
    ISharePointInteractiveAuthProvider,
    SharePointAuthPath,
    SharePointAuthService,
    SharePointMsalClientOptions,
} from "./SharePointAuthService";
import { SharePointGraphService } from "./SharePointGraphService";
import { SharePointNaaInteractiveAuthProvider } from "./SharePointNaaInteractiveAuthProvider";

/** Auth + Graph services bound to one SharePoint connection's Entra app/tenant. */
export interface SharePointConnectionServices {
    authService: SharePointAuthService;
    graphService: SharePointGraphService;
}

/** Overridable in tests so the factory can be exercised without real MSAL/crypto. */
export interface SharePointMsalClientFactories {
    createClient: (options: SharePointMsalClientOptions) => IPublicClientApplication;
    createNestableClient: (options: SharePointMsalClientOptions) => Promise<IPublicClientApplication>;
}

/**
 * Lazily builds and caches the MSAL client + interactive provider + auth/Graph services for each
 * distinct (tenantId, appId) pair found in a project's `projectFolderConnections`. The Entra
 * identifiers only become known once the project's settings load, so nothing MSAL-related is
 * created at startup. `usesNaa` is decided once by the composition root (`src/index.tsx`) and
 * must be the same flag `EntryPoint` uses to gate its redirect-leg bootstrap.
 */
export class SharePointAuthServiceFactory {
    private readonly servicesByKey = new Map<string, Promise<SharePointConnectionServices>>();

    constructor(
        private officeWrapper: OfficeWrapper,
        private configurationService: ConfigurationService,
        private httpRequestWrapper: HttpRequestWrapper,
        private logger: Logger,
        private analyticsManager: AnalyticsManager,
        private readonly usesNaa: boolean,
        private readonly msalClientFactories: SharePointMsalClientFactories = {
            createClient: createSharePointMsalClient,
            createNestableClient: createNestableSharePointMsalClient,
        }
    ) {}

    getServices(connection: ProjectFolderConnection): Promise<SharePointConnectionServices> {
        if (!connection.appId || !connection.tenantId) {
            return Promise.reject(
                new Error(`SharePoint connection "${connection.name}" is missing its Entra appId/tenantId`)
            );
        }
        const key = getMsalClientKey(connection);
        let services = this.servicesByKey.get(key);
        if (!services) {
            services = this.createServices(connection.appId, connection.tenantId);
            // Drop a failed creation so the next call can retry instead of replaying the rejection.
            services.catch(() => this.servicesByKey.delete(key));
            this.servicesByKey.set(key, services);
        } else {
            this.logger.info(
                `SharePointAuthServiceFactory: reusing the ${this.authPath} MSAL client already created for tenant ${connection.tenantId} (same tenant + app as a previous connection)`
            );
        }
        return services;
    }

    private get authPath(): SharePointAuthPath {
        return this.usesNaa ? "MSAL + NAA" : "MSAL-only";
    }

    private async createServices(clientId: string, tenantId: string): Promise<SharePointConnectionServices> {
        const options: SharePointMsalClientOptions = {
            clientId,
            authority: toMsalAuthority(tenantId),
            redirectUri: this.configurationService.sharePointMsalRedirectUrl,
        };
        this.logger.info(
            `SharePointAuthServiceFactory: creating MSAL client for tenant ${tenantId} - auth path = ${
                this.authPath
            } (${
                this.usesNaa
                    ? "createNestablePublicClientApplication, Office host brokers sign-in in-process"
                    : "PublicClientApplication, interactive step runs in an Office Dialog"
            })`
        );

        const msalClient = this.usesNaa
            ? await this.msalClientFactories.createNestableClient(options)
            : this.msalClientFactories.createClient(options);
        // Without a hint Entra shows "Pick an account" whenever the browser holds several sessions.
        const loginHint = this.officeWrapper.mailboxUserEmailAddress || undefined;
        const interactiveAuthProvider: ISharePointInteractiveAuthProvider = this.usesNaa
            ? new SharePointNaaInteractiveAuthProvider(msalClient, this.logger, loginHint)
            : new SharePointConsentDialog(
                  this.officeWrapper,
                  this.configurationService,
                  this.logger,
                  options,
                  loginHint
              );

        const authService = new SharePointAuthService(
            msalClient,
            interactiveAuthProvider,
            this.logger,
            undefined,
            this.analyticsManager,
            tenantId,
            this.authPath
        );
        const graphService = new SharePointGraphService(authService, this.httpRequestWrapper, this.logger);
        return { authService, graphService };
    }
}
